/**
 * Maps the gateway's snake_case auth payloads onto the camelCase `AuthUser`
 * shape the UI expects. Centralised here so the rest of the app never sees the
 * wire format.
 *
 * NOTE: field names follow the documented security-service contract; confirm
 * against the live `/auth/*` responses during real-mode verification and adjust
 * here only.
 */

import type { AuthUser } from '@/types/auth.types'
import type { UserRole, UserStatus } from '@/types/user.types'
import type { TenantType, PlanCode } from '@/types/tenant.types'
import { normalizeRole } from '@/security/accessContract'

/** Raw user object as returned by the gateway (best-effort superset). */
export interface RawAuthUser {
  id?: string
  user_id?: string
  email?: string
  username?: string
  first_name?: string
  last_name?: string
  full_name?: string
  role?: string
  status?: string
  is_verified?: boolean | null
  is_active?: boolean | null
  tenant_id?: string
  tenant_code?: string
  tenant_name?: string
  tenant_type?: string
  plan_code?: string
  avatar_url?: string
  phone?: string
  last_login_at?: string
  current_session_id?: string
  created_at?: string | null
  updated_at?: string | null
}

export interface RawLoginResponse {
  access_token?: string
  token?: string
  refresh_token?: string
  token_type?: string
  session_id?: string
  role?: string
  roles?: string[]
  permissions?: string[]
  features?: string[]
  role_compatibility?: Array<Record<string, unknown>>
  requires_role_classification?: boolean
  access_contract_version?: string
  /** Not present today — the gateway login response carries no user object. */
  user?: RawAuthUser
}

/** Decoded JWT claims emitted by the security service. */
export interface JwtClaims {
  sub?: string
  user_id?: string
  session_id?: string
  scope?: string
  role?: string
  roles?: string[]
  legacy_roles?: string[]
  requires_role_classification?: boolean
  access_contract_version?: string
  tenant_id?: string
  tenant_code?: string
  tenant_name?: string
  tenant_type?: string
  plan_code?: string
  exp?: number
  iat?: number
  iss?: string
  [key: string]: unknown
}

/**
 * Fallback client-side role when the JWT carries no role (e.g. a user with no
 * role assigned). The server enforces real authorization on every API call via
 * the JWT — this only drives which nav/pages the UI shows — so a least-privilege
 * fallback is the safe default.
 */
function coerceRole(value: unknown): UserRole {
  return normalizeRole(value)
}

/** Base64url-decode and parse a JWT's payload. Returns null on any failure. */
export function decodeJwtClaims(token: string): JwtClaims | null {
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    let b64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    b64 += '='.repeat((4 - (b64.length % 4)) % 4)
    const json = decodeURIComponent(
      atob(b64)
        .split('')
        .map(c => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join('')
    )
    return JSON.parse(json)
  } catch {
    return null
  }
}

/**
 * Build an AuthUser from JWT claims + the login identifier, since the gateway
 * login response has no user object and no profile endpoint works yet. Identity
 * (id, session) is real; name/role/tenant are best-effort placeholders.
 */
export function buildAuthUserFromClaims(
  claims: JwtClaims | null,
  identifier?: string,
  login?: RawLoginResponse
): AuthUser {
  const sub = typeof claims?.sub === 'string' ? claims.sub.replace(/^user:/, '') : undefined
  const id = claims?.user_id ?? sub ?? ''
  const email = identifier ?? ''
  const localPart = email.includes('@') ? email.split('@')[0] : email
  const fullName = localPart || 'User'

  return {
    authScope: claims?.scope ?? 'user',
    controllers: Array.isArray(claims?.controllers)
      ? (claims.controllers as AuthUser['controllers'])
      : [],
    id,
    email,
    firstName: fullName,
    lastName: '',
    fullName,
    // Real role from the JWT claims (security service now embeds it); falls back
    // to least-privilege if absent.
    role: coerceRole(claims?.role ?? claims?.roles?.[0] ?? login?.role),
    roles: (claims?.roles ?? login?.roles ?? []).map(coerceRole),
    permissions: Array.isArray(claims?.permissions)
      ? claims.permissions.filter((value): value is string => typeof value === 'string')
      : (login?.permissions ?? []),
    features: Array.isArray(claims?.features)
      ? claims.features.filter((value): value is string => typeof value === 'string')
      : (login?.features ?? []),
    roleCompatibility: login?.role_compatibility ?? [],
    requiresRoleClassification:
      login?.requires_role_classification ?? claims?.requires_role_classification ?? false,
    accessContractVersion: login?.access_contract_version ?? claims?.access_contract_version,
    status:
      login?.user?.is_active === true
        ? 'active'
        : login?.user?.is_active === false
          ? 'inactive'
          : 'unknown',
    isVerified: login?.user?.is_verified ?? null,
    tenantId: claims?.tenant_id ?? '',
    tenantCode: '',
    tenantName: login?.user?.tenant_name ?? '',
    tenantType:
      (claims?.tenant_type as TenantType) ??
      (login?.user?.tenant_type as TenantType) ??
      'medium_firm',
    planCode:
      (claims?.plan_code as PlanCode) ?? (login?.user?.plan_code as PlanCode) ?? 'professional',
    currentSessionId: claims?.session_id ?? login?.session_id,
    createdAt: login?.user?.created_at ?? null,
    updatedAt: login?.user?.updated_at ?? null,
  }
}

export function mapAuthUser(raw: RawAuthUser): AuthUser {
  const firstName = raw.first_name ?? ''
  const lastName = raw.last_name ?? ''
  const fullName = raw.full_name ?? `${firstName} ${lastName}`.trim()

  return {
    id: raw.id ?? raw.user_id ?? '',
    email: raw.email ?? '',
    username: raw.username,
    firstName,
    lastName,
    fullName,
    role: coerceRole(raw.role),
    status:
      raw.is_active === true
        ? 'active'
        : raw.is_active === false
          ? 'inactive'
          : ['active', 'inactive', 'pending', 'locked'].includes(raw.status ?? '')
            ? (raw.status as UserStatus)
            : 'unknown',
    isVerified: raw.is_verified ?? null,
    tenantId: raw.tenant_id ?? '',
    tenantCode: raw.tenant_code ?? '',
    tenantName: raw.tenant_name ?? '',
    tenantType: (raw.tenant_type as TenantType) ?? 'solo_ca',
    planCode: (raw.plan_code as PlanCode) ?? 'starter',
    avatarUrl: raw.avatar_url,
    phone: raw.phone,
    lastLoginAt: raw.last_login_at,
    currentSessionId: raw.current_session_id,
    createdAt: raw.created_at ?? null,
    updatedAt: raw.updated_at ?? null,
  }
}
